import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// icons
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';
import VectorIcon from '../../../assets/images/svg/vector.svg';

// tools
import { extractNumericQuantity, filterMaterialData } from '../../tools/manager';

// components
import Modal from '../../components/Modal_3';
import Table from '../Table';

// ligne vide
const refMaterial = {
  idMaterial: '',
  nature: '',
  material: '',
  refName: '',
  quantity: '',       // numérique (envoyé au back)
  quantityLabel: '',  // ce que l'utilisateur tape ("3 u", "250 ml"...)
  pose: '',
  depot: '',
  surface: '',
  statut: false,
};

const PrefillOrder = () => {
  const navigate = useNavigate();
  const { idCustomer, idWorksite } = useParams();

  // state
  const [customer, setCustomer] = useState();
  const [bcCtm, setBcCtm] = useState('');
  const [listeMaterialCreate, setListeMaterialCreate] = useState([{ ...refMaterial }]);
  const [formError, setFormError] = useState('');
  const [materials, setMaterials] = useState([]);
  const [referencesMaterials, setReferencesMaterials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [numeroBon, setNumeroBon] = useState(null);

  // store actions
  const getListMaterialSettings = useStoreActions(
    actions => actions.middleware.OrdersMiddleware.getListMaterialSettings
  );
  const getCompany = useStoreActions(
    actions => actions.middleware.CompanyMiddleware.getCompany
  );
  const getNextCounterValue = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.getNextCounterValue
  );
  const createPreInterv = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.createPreInterv
  );

  // ====== API CALLS ======

  const callListMaterialSettings = async () => {
    try {
      const res = await getListMaterialSettings();
      const data = res.filter(item => item.name !== 'Climatiseur');
      setReferencesMaterials(data);
      setMaterials(data);
    } catch (error) {
      console.log('Error: ', error);
      window.location.href = '/clients';
    }
  };

  const callGetCompany = async () => {
    try {
      const res = await getCompany(idCustomer);
      setCustomer(res);
    } catch (error) {
      console.log('Error: ', error);
      window.location.href = '/clients';
    }
  };

  const callCreatePreInterventionCoupon = async data => {
    try {
      await createPreInterv(data);
      setShowModal(true);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #2 Une erreur est survenue lors de la création du bon');
    }
  };

  const callLastNumberCoupon = async data => {
    try {
      // 1. Récupérer le prochain numéro côté backend
      const res = await getNextCounterValue("couponPreInter"); // ex: "124"
      const newData = { ...data, numero: res };
      setNumeroBon(res);
      await callCreatePreInterventionCoupon(newData);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #1 Une erreur est survenue lors de la création du bon');
    }
  };

  // ====== FORM LOGIC ======

  const addMaterial = e => {
    e.preventDefault();
    // on clone refMaterial pour éviter le partage de référence
    setListeMaterialCreate(prev => [...prev, { ...refMaterial }]);
  };

  const deleteMaterial = (e, index) => {
    e.preventDefault();
    setListeMaterialCreate(prev => prev.filter((_, i) => i !== index));
  };

  const changeValueMaterial = (index, key, value) => {
    setListeMaterialCreate(prev => {
      const copy = prev.map(item => ({ ...item }));
      const line = copy[index];
      if (!line) return prev;

      if (key === 'material') {
        // on retrouve le matériel, on met l'id et le nom
        const selected = referencesMaterials.find(el => el.name === value);
        line.material = value;
        line.idMaterial = selected?.id || '';
        line.refName = ''; // reset ref si changement de matériel
      } else if (key === 'quantityLabel') {
        // champ libre : "3 u", "250 ml", etc.
        line.quantityLabel = value;
        const numeric = extractNumericQuantity(value);
        line.quantity = numeric === '' ? '' : numeric;
      } else {
        line[key] = value;
      }

      copy[index] = line;
      return copy;
    });
  };

  const resetFormError = () => setFormError('');

  const isOkForm = () => {
    if (!bcCtm.trim()) {
      setFormError('Entrer un numéro de bon CTM');
      return false;
    }
    if (!listeMaterialCreate.length) {
      setFormError('Ajouter une intervention !');
      return false;
    }

    for (const element of listeMaterialCreate) {
      if (!element.nature.trim()) {
        setFormError("Défaut d'une nature d'intervention");
        return false;
      }
      if (!element.material.trim()) {
        setFormError("Défaut d'un nom de matériel");
        return false;
      }
      if (!element.refName.trim()) {
        setFormError("Défaut d'une référence");
        return false;
      }
      if (!element.quantityLabel?.trim()) {
        setFormError("Défaut d'une quantité (ex : 3, 10 u, 250 ml)");
        return false;
      }
      const numeric = extractNumericQuantity(element.quantityLabel);
      if (numeric === '' || Number(numeric) <= 0) {
        setFormError("Quantité invalide (ex : 3, 10 u, 250 ml)");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = e => {
    e.preventDefault();

    // On s'assure que quantity est bien numérisée avant envoi
    const materialsWithNumeric = listeMaterialCreate.map(line => {
      const numeric = extractNumericQuantity(line.quantityLabel || '');
      return {
        ...line,
        quantity: numeric === '' ? null : numeric,
      };
    });

    const newdata = {
      ctm: bcCtm,
      couponPreInterWorksitesId: idWorksite,
      materials: filterMaterialData(materialsWithNumeric),
    };

    if (isOkForm()) {
      callLastNumberCoupon(newdata);
    }
  };

  const goBack = () => {
    setShowModal(false);
    navigate(-1);
  };

  // ====== EFFECTS ======

  useEffect(() => {
    resetFormError();
  }, [bcCtm, listeMaterialCreate]);

  useEffect(() => {
    callGetCompany();
    callListMaterialSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== HELPERS ======

  const getReferencesForLine = line => {
    const mat = referencesMaterials.find(m => m.id === line.idMaterial);
    const refStr = mat?.references?.[0]?.reference || '';
    return refStr
      .split(',')
      .map(r => r.trim())
      .filter(Boolean);
  };

  // ====== RENDER ======

  return (
    <section className="pt-14">
      <form onSubmit={handleSubmit}>
        {/* Infos client / BC CTM */}
        <div className="flex flex-col lg:flex-row lg:space-x-8 mx-14">
          <div className="flex flex-col space-y-4">
            <label htmlFor="client">Nom du client</label>
            <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
              {customer?.name}
            </p>
          </div>

          <div className="flex flex-col space-y-4">
            <label htmlFor="bcCtm">*BC CTM</label>
            <input
              id="bcCtm"
              className="w-[214px] rounded-lg placeholder:text-blue-admin py-4 border border-white-admin input"
              type="text"
              placeholder="N°"
              value={bcCtm}
              onChange={e => setBcCtm(e.target.value)}
            />
          </div>
        </div>

        {/* Tableau des interventions */}
        <div className="mt-10 mx-14 overflow-x-auto">
          <h5 className="font-semibold mb-7">Etat des lieux de l'intervention</h5>

          {!!listeMaterialCreate.length && (
            <Table>
              <thead>
                <tr>
                  <th>*Nature de l'intervention</th>
                  <th>*Nom du matériel</th>
                  <th>*Référence</th>
                  <th>Surface</th>
                  <th>Lieu de pose</th>
                  <th>Lieu de dépose</th>
                  <th>*Quantité</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {listeMaterialCreate.map((line, index) => {
                  const references = getReferencesForLine(line);

                  return (
                    <tr key={index}>
                      {/* Nature (champ libre) */}
                      <td className="border border-white border-r-8">
                        <input
                          className="mt-3 w-[240px] rounded-lg placeholder:text-blue-admin py-4 border border-white-admin text-blue-admin inputFocus"
                          type="text"
                          placeholder="Nature de l'intervention"
                          value={line.nature}
                          onChange={e => changeValueMaterial(index, 'nature', e.target.value)}
                        />
                      </td>

                      {/* Nom du matériel */}
                      <td className="border border-white border-x-8">
                        <select
                          className="rounded-lg py-4 border border-white-admin text-blue-admin input mt-3 w-[234px]"
                          value={line.material}
                          onChange={e => changeValueMaterial(index, 'material', e.target.value)}
                        >
                          <option disabled value="">
                            - Nom du matériel -
                          </option>
                          {materials.map(material => (
                            <option key={material.id} value={material.name}>
                              {material.name}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Référence */}
                      <td className="border border-white border-x-8">
                        <select
                          className="rounded-lg py-4 border border-white-admin text-blue-admin input mt-3 w-[234px]"
                          value={line.refName}
                          onChange={e => changeValueMaterial(index, 'refName', e.target.value)}
                          disabled={!references.length}
                        >
                          <option disabled value="">
                            -
                          </option>
                          {references.map(ref => (
                            <option key={ref} value={ref}>
                              {ref}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Surface */}
                      <td className="border border-x-8 border-white">
                        <input
                          className="rounded-lg py-4 border border-white-admin input mt-3"
                          type="number"
                          placeholder="-"
                          value={line.surface}
                          onChange={e => changeValueMaterial(index, 'surface', e.target.value)}
                        />
                      </td>

                      {/* Lieu de pose */}
                      <td className="border border-x-8 border-white">
                        <input
                          className="rounded-lg py-4 border border-white-admin input mt-3"
                          type="text"
                          placeholder="-"
                          value={line.pose}
                          onChange={e => changeValueMaterial(index, 'pose', e.target.value)}
                        />
                      </td>

                      {/* Lieu de dépose */}
                      <td className="border border-x-8 border-white">
                        <input
                          className="rounded-lg py-4 border border-white-admin input mt-3"
                          type="text"
                          placeholder="-"
                          value={line.depot}
                          onChange={e => changeValueMaterial(index, 'depot', e.target.value)}
                        />
                      </td>

                      {/* Quantité (champ libre + parse) */}
                      <td className="border border-x-8 border-white">
                        <input
                          className="rounded-lg py-4 border border-white-admin input mt-3"
                          type="text"
                          placeholder="Ex : 3, 10 u, 250 ml"
                          value={line.quantityLabel}
                          onChange={e => changeValueMaterial(index, 'quantityLabel', e.target.value)}
                        />
                      </td>

                      {/* Delete */}
                      <td className="text-center border border-white border-x-8">
                        <button
                          type="button"
                          className="w-8 p-2 border rounded-lg border-white-admin"
                          onClick={e => deleteMaterial(e, index)}
                        >
                          <img src={DeleteIcon} alt="delete icon" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          <div className="mt-5">
            <button
              type="button"
              className="w-[280px] py-3 text-sm text-blue-admin rounded-lg border border-white-admin mb-2"
              onClick={addMaterial}
            >
              + Ajouter une nouvelle intervention
            </button>
          </div>
        </div>

        {!!formError && (
          <p className="text-red-error text-end mt-5 mr-20 italic">{formError}</p>
        )}

        {/* Footer actions */}
        <div className="mt-16 py-4 flex justify-between items-center border-t border-white-admin">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="border rounded-lg cursor-pointer ml-14 border-gray-admin px-5 py-3 text-blue-admin"
          >
            Annuler
          </button>

          <div className="cursor-pointer mr-14 bg-yellow-admin rounded-lg">
            <button type="submit" className="py-3 px-5 text-blue-admin">
              Enregistrer ce bon pré-rempli
            </button>
          </div>
        </div>
      </form>

      <Modal
        showModal={showModal}
        title={`Bon d'intervention n°${numeroBon}`}
        text="Le bon d'intervention pré rempli est bien enregistré !"
        image={VectorIcon}
      >
        <button
          className="px-4 py-3 text-white rounded-lg cursor-pointer bg-blue-admin w-full"
          onClick={goBack}
        >
          J'ai compris
        </button>
      </Modal>
    </section>
  );
};

export default PrefillOrder;
